import { useState, useEffect, useCallback } from 'react';
import { rmmAPI, usersAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, ShieldAlert, Cloud, Trash2, RefreshCw, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { fmtWhen } from './rmmUtils';

const err = (e, m) => toast.error(e.response?.data?.detail || m);
const Spin = () => <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-cyan-500" /></div>;
const ScoreBadge = ({ v }) => (
  <Badge className={v >= 80 ? 'bg-emerald-100 text-emerald-700' : v >= 50 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}>{v}</Badge>
);
const M365_STATES = { connected: 'bg-emerald-100 text-emerald-700', simulated: 'bg-violet-100 text-violet-700', consent_required: 'bg-amber-100 text-amber-700', token_expired: 'bg-red-100 text-red-700', permission_insufficient: 'bg-red-100 text-red-700', error: 'bg-red-100 text-red-700', pending: 'bg-slate-100 text-slate-600' };

export const SecurityTab = ({ isAdmin }) => {
  const [posture, setPosture] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [weights, setWeights] = useState(null);
  const [editW, setEditW] = useState(false);
  const [tForm, setTForm] = useState({ customer_id: '', tenant_id: '', client_id: '', client_secret: '', label: '', simulated: false });

  const load = useCallback(() => {
    Promise.all([rmmAPI.securityPosture(), rmmAPI.m365Tenants(), rmmAPI.scoreWeights()])
      .then(([p, t, w]) => { setPosture(p.data); setTenants(t.data); setWeights(w.data); })
      .catch(() => setPosture({ devices: [], customers: [] }));
  }, []);
  useEffect(() => { load(); usersAPI.list('customer').then((r) => setCustomers(r.data)).catch(() => {}); }, [load]);

  if (!posture) return <Spin />;

  return (
    <div className="space-y-6" data-testid="rmm-security-tab">
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <Card className="bg-white border-slate-200">
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <CardTitle className="text-base flex items-center gap-2"><ShieldAlert className="w-4 h-4 text-cyan-500" />Security Posture by Customer</CardTitle>
            {isAdmin && <Button size="sm" variant="outline" onClick={() => setEditW(!editW)} data-testid="sec-weights-btn">Scoring weights</Button>}
          </CardHeader>
          <CardContent className="space-y-2" data-testid="sec-posture-customers">
            {editW && weights && (
              <div className="p-3 rounded-xl border border-cyan-200 bg-cyan-50/40 grid grid-cols-4 gap-2">
                {Object.entries(weights).map(([k, v]) => (
                  <label key={k} className="text-[11px] text-slate-600 capitalize">{k.replace('_', ' ')}
                    <Input type="number" value={v} className="h-7 bg-white mt-0.5" data-testid={`sec-weight-${k}`}
                      onChange={(e) => setWeights({ ...weights, [k]: parseInt(e.target.value || 0, 10) })} />
                  </label>))}
                <Button size="sm" className="bg-cyan-600 col-span-4 h-7" data-testid="sec-weights-save"
                  onClick={() => rmmAPI.setScoreWeights(weights).then(() => { toast.success('Weights saved — transparent scoring updated'); setEditW(false); load(); }).catch((e) => err(e, 'Failed'))}>Save weights</Button>
              </div>)}
            {posture.customers.map((c) => (
              <div key={c.customer_id} className="p-2.5 rounded-lg border border-slate-200" data-testid={`sec-posture-${c.customer_name}`}>
                <div className="flex items-center justify-between text-sm">
                  <span className="font-medium text-slate-800">{c.customer_name}</span>
                  <span className="text-xs text-slate-500">{c.devices} device(s)</span>
                  <ScoreBadge v={c.score} />
                </div>
                {c.remediation.length > 0 && (
                  <div className="mt-1.5 flex flex-wrap gap-1">{c.remediation.slice(0, 4).map((r, i) => <Badge key={i} variant="outline" className="text-[10px] text-amber-700 border-amber-200">{r}</Badge>)}</div>)}
              </div>))}
          </CardContent>
        </Card>

        <Card className="bg-white border-slate-200">
          <CardHeader><CardTitle className="text-base">Device Security Details</CardTitle></CardHeader>
          <CardContent className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs text-slate-500 border-b border-slate-200">
                <th className="py-2 pr-2">Device</th><th className="pr-2">Score</th><th className="pr-2">AV</th><th className="pr-2">Firewall</th><th className="pr-2">Encryption</th><th className="pr-2">Backup</th><th className="pr-2">Admins</th><th>Patching</th></tr></thead>
              <tbody data-testid="sec-device-rows">
                {posture.devices.slice(0, 20).map((d) => (
                  <tr key={d.device_id} className="border-b border-slate-100" data-testid={`sec-device-${d.hostname}`}>
                    <td className="py-1.5 pr-2 font-medium text-slate-800">{d.hostname}</td>
                    <td className="pr-2"><ScoreBadge v={d.score} /></td>
                    {['antivirus', 'firewall', 'encryption', 'backup', 'local_admins', 'patching'].map((k) => (
                      <td key={k} className="pr-2">
                        <span className={`text-xs font-semibold ${d.checks[k] >= 99 ? 'text-emerald-600' : d.checks[k] >= 50 ? 'text-amber-600' : 'text-red-600'}`}>{d.checks[k]}%</span>
                      </td>))}
                  </tr>))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      </div>

      <Card className="bg-white border-slate-200">
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><Cloud className="w-4 h-4 text-cyan-500" />Microsoft 365 Tenants</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          {isAdmin && (
            <div className="p-3 rounded-xl border border-cyan-200 bg-cyan-50/40 grid grid-cols-2 md:grid-cols-6 gap-2 items-center">
              <Select value={tForm.customer_id} onValueChange={(v) => setTForm({ ...tForm, customer_id: v })}>
                <SelectTrigger className="bg-white" data-testid="m365-customer-select"><SelectValue placeholder="Customer" /></SelectTrigger>
                <SelectContent>{customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
              <Input placeholder="Tenant ID" value={tForm.tenant_id} onChange={(e) => setTForm({ ...tForm, tenant_id: e.target.value })} className="bg-white" data-testid="m365-tenant-input" />
              <Input placeholder="App (client) ID" value={tForm.client_id} onChange={(e) => setTForm({ ...tForm, client_id: e.target.value })} className="bg-white" data-testid="m365-clientid-input" />
              <Input type="password" placeholder="Client secret" value={tForm.client_secret} onChange={(e) => setTForm({ ...tForm, client_secret: e.target.value })} className="bg-white" data-testid="m365-secret-input" />
              <label className="flex items-center gap-1.5 text-xs text-slate-600"><Checkbox checked={tForm.simulated} onCheckedChange={(v) => setTForm({ ...tForm, simulated: !!v })} data-testid="m365-simulate-check" />Demo</label>
              <Button size="sm" className="bg-cyan-600 hover:bg-cyan-700" data-testid="m365-add-btn"
                onClick={() => {
                  if (!tForm.customer_id || !tForm.tenant_id) return toast.error('Customer and tenant ID required');
                  rmmAPI.createM365Tenant(tForm).then(() => { toast.success('Tenant added'); setTimeout(load, 1500); }).catch((e) => err(e, 'Failed'));
                }}><Plus className="w-4 h-4" /></Button>
            </div>)}
          {tenants.length === 0 ? <p className="text-sm text-slate-500 py-2 text-center">No tenants connected. Register an Entra app (least-privilege Graph permissions) and add it here.</p> :
            tenants.map((t) => (
              <div key={t.id} className="p-3 rounded-xl border border-slate-200 space-y-2" data-testid={`m365-tenant-${t.tenant_id}`}>
                <div className="flex items-center justify-between text-sm">
                  <div><span className="font-medium text-slate-800">{t.label || t.tenant_id}</span>
                    <span className="text-xs text-slate-500 ml-2">{t.customer_name} · checked {fmtWhen(t.last_checked)}</span></div>
                  <div className="flex items-center gap-1.5">
                    <Badge className={M365_STATES[t.state] || 'bg-slate-100 text-slate-600'} data-testid={`m365-state-${t.tenant_id}`}>{t.state.replace('_', ' ')}</Badge>
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => rmmAPI.checkM365Tenant(t.id).then(load)} data-testid={`m365-check-${t.tenant_id}`}><RefreshCw className="w-3.5 h-3.5" /></Button>
                    {isAdmin && <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500" onClick={() => rmmAPI.deleteM365Tenant(t.id).then(load)}><Trash2 className="w-3.5 h-3.5" /></Button>}
                  </div>
                </div>
                {t.data?.service_health && (
                  <div className="flex flex-wrap gap-1.5">
                    {t.data.service_health.map((h, i) => (
                      <Badge key={i} className={h.status === 'serviceOperational' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-amber-50 text-amber-700 border border-amber-200'}>{h.service}: {h.status === 'serviceOperational' ? 'OK' : h.status}</Badge>))}
                  </div>)}
                {t.data?.skus && (
                  <p className="text-xs text-slate-500">
                    {t.data.users_total != null && <>Users: <b>{t.data.users_total}</b> · </>}
                    {t.data.mfa_enabled_pct != null && <>MFA enabled: <b>{t.data.mfa_enabled_pct}%</b> · </>}
                    Licenses: {t.data.skus.map((s) => `${s.sku} ${s.assigned}/${s.total}`).join(', ')}
                    {t.data.unassigned_licenses ? <span className="text-amber-600"> · {t.data.unassigned_licenses} unassigned (savings opportunity)</span> : null}
                  </p>)}
                {t.last_error && <p className="text-xs text-red-600">{t.last_error}</p>}
              </div>))}
        </CardContent>
      </Card>
    </div>
  );
};
